import { useState } from "react";
import type { OrderResponse } from "@hybrid-escrow/shared";
import { apiClient } from "../lib/api-client";
import { describeError } from "./States";

/**
 * Buyer-facing guidance + code reveal, and seller-facing code submission, for a DOMAIN_TRANSFER
 * order. Simpler than AccountTransferPanel by design — see services/domainTransfers.ts's
 * docblock for why there's no "platform verifying" stopover here: nothing about a transfer/EPP
 * code can be meaningfully checked without actually attempting a real transfer, which happens at
 * the buyer's own registrar, not something this platform can do on anyone's behalf.
 */

const STAGE_COPY: Record<string, { title: string; detail: string }> = {
  AWAITING_SELLER_CODE: {
    title: "Waiting on the seller",
    detail:
      "The seller needs to retrieve the domain's transfer (EPP/auth) code from their registrar and submit it here.",
  },
  READY_FOR_BUYER: {
    title: "Your transfer code is ready",
    detail:
      "Reveal it below, then start the transfer at your own registrar using this code — before releasing funds, confirm the transfer actually completes.",
  },
  CODE_REVEALED: {
    title: "You've claimed the transfer code",
    detail:
      "Once the domain has fully transferred to you and resolves as expected, release funds below.",
  },
};

/** The seller's submission — hands the domain's real transfer code to MarketOnBase, which
 *  auto-advances the order straight to READY_FOR_BUYER (no verification stopover — see the panel
 *  docblock). Its own component so draft state survives the order refetch this triggers. */
function SellerCodeForm({
  orderId,
  alreadySubmitted,
  onSubmitted,
}: {
  orderId: string;
  alreadySubmitted: boolean;
  onSubmitted: () => void;
}) {
  const [transferCode, setTransferCode] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isDone = justSubmitted || alreadySubmitted;

  async function handleSubmit() {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await apiClient.submitDomainTransferCode(orderId, {
        transferCode: transferCode.trim(),
        notes: notes.trim() || undefined,
      });
      setJustSubmitted(true);
      setIsEditing(false);
      onSubmitted();
    } catch (err) {
      setSubmitError(describeError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isDone && !isEditing) {
    return (
      <div className="stack">
        <p className="text-sm muted">Submitted — the buyer can now reveal it from their order.</p>
        <div className="row">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setIsEditing(true)}>
            Made a mistake? Submit a correction
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <p className="text-sm muted">
        Retrieve the domain's transfer (sometimes called EPP or auth) code from your registrar's
        control panel, then submit it below.
      </p>
      <div className="field" style={{ marginBottom: 0 }}>
        <label htmlFor="domain-transfer-code">Transfer / EPP code</label>
        <input
          id="domain-transfer-code"
          type="text"
          value={transferCode}
          onChange={(e) => setTransferCode(e.target.value)}
          maxLength={500}
        />
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label htmlFor="domain-transfer-notes">Notes (optional)</label>
        <textarea
          id="domain-transfer-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={4000}
          placeholder="e.g. Unlocked at the registrar; may take a day for the lock to clear."
        />
      </div>
      {submitError && (
        <p className="field-error-text" role="alert">
          {submitError}
        </p>
      )}
      <div className="row">
        <button
          type="button"
          className="btn btn-primary"
          disabled={isSubmitting || !transferCode.trim()}
          onClick={() => void handleSubmit()}
        >
          {isSubmitting ? "Submitting…" : "Submit transfer code"}
        </button>
      </div>
    </div>
  );
}

export function DomainTransferPanel({
  order,
  isBuyer,
  isSeller,
  onOrderChanged,
}: {
  order: OrderResponse;
  isBuyer: boolean;
  isSeller: boolean;
  onOrderChanged: () => void;
}) {
  const [revealed, setRevealed] = useState<{ transferCode: string; notes: string | null } | null>(
    null,
  );
  const [isRevealing, setIsRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);

  const stage = order.domainTransferStage;
  if (!stage) return null;
  const copy = STAGE_COPY[stage] ?? STAGE_COPY.AWAITING_SELLER_CODE;

  async function handleReveal() {
    setRevealError(null);
    setIsRevealing(true);
    try {
      const result = await apiClient.revealDomainTransferCode(order.id);
      setRevealed(result);
    } catch (err) {
      setRevealError(describeError(err));
    } finally {
      setIsRevealing(false);
    }
  }

  const canSubmitCode =
    isSeller && (stage === "AWAITING_SELLER_CODE" || stage === "READY_FOR_BUYER");
  const canReveal =
    isBuyer && (stage === "READY_FOR_BUYER" || stage === "CODE_REVEALED") && !revealed;

  return (
    <div className="card stack">
      <h2>Domain handoff</h2>
      <div className="alert alert--info" role="status">
        <strong>{copy?.title}</strong>
        <p>{copy?.detail}</p>
      </div>

      {canSubmitCode && (
        <SellerCodeForm
          orderId={order.id}
          alreadySubmitted={stage !== "AWAITING_SELLER_CODE"}
          onSubmitted={onOrderChanged}
        />
      )}

      {canReveal && (
        <button
          type="button"
          className="btn btn-primary"
          disabled={isRevealing}
          onClick={() => void handleReveal()}
        >
          {isRevealing ? "Revealing…" : "Reveal transfer code"}
        </button>
      )}

      {revealError && (
        <p className="field-error-text" role="alert">
          {revealError}
        </p>
      )}

      {revealed && (
        <div className="card stack success-pop">
          <span className="row" style={{ gap: "0.5rem" }}>
            <span className="success-check" aria-hidden="true">
              ✓
            </span>
            <span className="muted text-sm">Your transfer code</span>
          </span>
          <div className="row row--between">
            <span className="muted text-sm">Transfer / EPP code</span>
            <span className="mono">{revealed.transferCode}</span>
          </div>
          {revealed.notes && (
            <div className="stack" style={{ gap: "var(--space-1)" }}>
              <span className="muted text-sm">Notes</span>
              <p className="text-sm" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {revealed.notes}
              </p>
            </div>
          )}
          <p className="text-sm muted">
            Start the transfer at your own registrar using this code — confirm it actually
            completes and the domain resolves as expected before releasing funds.
          </p>
        </div>
      )}
    </div>
  );
}
